import { useState } from "react";
import { useCentralStore } from "../../../../store/Central";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";

export function isConfigTrue(config: any, option: string): boolean {
	return (
		config &&
		typeof config === "object" &&
		option in config &&
		config[option] === true
	);
}

export const KonfigurationseinstellungenPage: React.FC = () => {
	const { organization, updateOrganizationConfig } = useCentralStore(
		(state) => ({
			organization: state.organization,
			updateOrganizationConfig: state.updateOrganizationConfig,
		})
	);

	const config = organization?.config as any;
	const customPath = config?.custom_path ?? false;
	const showCodes = config?.sidebar_type ?? "none";
	const showInsuranceOnPdf = config?.is_show_insurance_on_pdf ?? false;
	const sortClientsByCode = config?.sort_clients_by_code ?? false;
	const zakEnabled = config?.zak_enabled ?? false;
	const zakAutoMarkPaid = config?.zak_auto_mark_paid ?? false;
	const zakDownloadArchive = config?.zak_download_archive ?? false;
	const catalogOnlyMode = config?.catalog_only_mode ?? false;
	const technicianAppliesToArticles =
		config?.technician_applies_to_articles ?? false;
	const patientDivergenceEnabled =
		config?.patient_divergence_enabled !== false;
	const patientDivergenceThreshold =
		Number(config?.patient_divergence_threshold) || 2;
	const patientDivergenceIgnoreCode =
		config?.patient_divergence_ignore_code === true;
	const backgroundDoubleClickFullscreenEnabled =
		config?.background_double_click_fullscreen_enabled !== false;
	const untouchedJobsRequireNoDocuments =
		config?.untouched_jobs_require_no_documents === true;
	const [thresholdInput, setThresholdInput] = useState<string>(
		String(patientDivergenceThreshold)
	);

	const handleCustomPathChange = async (checked: boolean) => {
		await updateOrganizationConfig("custom_path", checked);
	};

	const handleShowCodesChange = async (checked: boolean) => {
		await updateOrganizationConfig(
			"sidebar_type",
			checked ? "codes" : "none"
		);
	};

	const handleShowInsuranceOnPdfChange = async (checked: boolean) => {
		await updateOrganizationConfig("is_show_insurance_on_pdf", checked);
	};

	const handleSortClientsByCodeChange = async (checked: boolean) => {
		await updateOrganizationConfig("sort_clients_by_code", checked);
	};

	const handleZakEnabledChange = async (checked: boolean) => {
		await updateOrganizationConfig("zak_enabled", checked);
	};

	const handleZakAutoMarkPaidChange = async (checked: boolean) => {
		await updateOrganizationConfig("zak_auto_mark_paid", checked);
	};

	const handleZakDownloadArchiveChange = async (checked: boolean) => {
		await updateOrganizationConfig("zak_download_archive", checked);
	};

	const handleCatalogOnlyModeChange = async (checked: boolean) => {
		await updateOrganizationConfig("catalog_only_mode", checked);
	};

	const handleTechnicianAppliesToArticlesChange = async (
		checked: boolean
	) => {
		await updateOrganizationConfig(
			"technician_applies_to_articles",
			checked
		);
	};

	const handlePatientDivergenceEnabledChange = async (checked: boolean) => {
		await updateOrganizationConfig("patient_divergence_enabled", checked);
	};

	const handlePatientDivergenceThresholdCommit = async () => {
		const parsed = Math.max(1, Math.floor(Number(thresholdInput) || 2));
		setThresholdInput(String(parsed));
		if (parsed === patientDivergenceThreshold) return;
		await updateOrganizationConfig("patient_divergence_threshold", parsed);
	};

	const handlePatientDivergenceIgnoreCodeChange = async (
		checked: boolean
	) => {
		await updateOrganizationConfig(
			"patient_divergence_ignore_code",
			checked
		);
	};

	const handleBackgroundDoubleClickFullscreenChange = async (
		checked: boolean
	) => {
		await updateOrganizationConfig(
			"background_double_click_fullscreen_enabled",
			checked
		);
	};

	const handleUntouchedJobsRequireNoDocumentsChange = async (
		checked: boolean
	) => {
		await updateOrganizationConfig(
			"untouched_jobs_require_no_documents",
			checked
		);
	};

	return (
		<div className="p-6">
			<div className="max-w-6xl">
				<h1 className="text-2xl font-semibold text-gray-900 mb-6">
					Konfigurationseinstellungen
				</h1>
				<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
					<div className="flex flex-col space-y-6 w-full max-w-2xl">
						{/* Custom Path Setting */}
						<div className="flex items-start space-x-3">
							<Checkbox
								id="custom-path"
								checked={customPath == true}
								onCheckedChange={handleCustomPathChange}
								className="mt-1"
							/>
							<div className="space-y-1">
								<Label
									htmlFor="custom-path"
									className="text-sm font-medium cursor-pointer"
								>
									Benutzerdefinierten Download-Pfad nutzen
								</Label>
								<p className="text-xs text-gray-600">
									Es wird bei jedem Download nach dem
									Zielordner gefragt. Wenn diese Option
									deaktiviert ist, wird der Standard-Ordner
									des Browser verwendet.
								</p>
							</div>
						</div>

						{/* Show Codes Setting */}
						<div className="flex items-start space-x-3">
							<Checkbox
								id="show-codes"
								checked={showCodes == "codes"}
								onCheckedChange={handleShowCodesChange}
								className="mt-1"
							/>
							<div className="space-y-1">
								<Label
									htmlFor="show-codes"
									className="text-sm font-medium cursor-pointer"
								>
									Kürzel verwenden
								</Label>
								<p className="text-xs text-gray-600">
									Statt der Initialen des Kunden wird das
									Kürzel zur Anzeige in der Seitenleiste
									verwendet.
								</p>
							</div>
						</div>

						{/* Show Insurance on PDF Setting */}
						<div className="flex items-start space-x-3">
							<Checkbox
								id="show-insurance-on-pdf"
								checked={showInsuranceOnPdf == true}
								onCheckedChange={handleShowInsuranceOnPdfChange}
								className="mt-1"
							/>
							<div className="space-y-1">
								<Label
									htmlFor="show-insurance-on-pdf"
									className="text-sm font-medium cursor-pointer"
								>
									Versicherungsinformation auf KV/LS anzeigen
								</Label>
								<p className="text-xs text-gray-600">
									Zeigt die Versicherungsinformationen auf
									Kostenvoranschlägen und Lieferscheinen an.
								</p>
							</div>
						</div>

						{/* Sort Clients by Code Setting */}
						<div className="flex items-start space-x-3">
							<Checkbox
								id="sort-clients-by-code"
								checked={sortClientsByCode == true}
								onCheckedChange={handleSortClientsByCodeChange}
								className="mt-1"
							/>
							<div className="space-y-1">
								<Label
									htmlFor="sort-clients-by-code"
									className="text-sm font-medium cursor-pointer"
								>
									Kunden nach Kürzel sortieren
								</Label>
								<p className="text-xs text-gray-600">
									Sortiert Kunden in der Seitenleiste nach
									ihrem Kürzel anstelle des Nachnamens.
									Favoriten bleiben weiterhin oben.
								</p>
							</div>
						</div>

						{/* ZAK Integration Setting */}
						<div className="flex items-start space-x-3">
							<Checkbox
								id="zak-enabled"
								checked={zakEnabled == true}
								onCheckedChange={handleZakEnabledChange}
								className="mt-1"
							/>
							<div className="space-y-1">
								<Label
									htmlFor="zak-enabled"
									className="text-sm font-medium cursor-pointer"
								>
									ZAK Integration
								</Label>
								<p className="text-xs text-gray-600">
									Aktiviert die Möglichkeit, Rechnungen direkt
									an die ZAK (Zahnärztekasse) zu senden. Dies
									zeigt den "An ZAK senden" Button in der
									Debitoren-Ansicht an.
								</p>
							</div>
						</div>

						{/* ZAK Auto Mark as Paid Setting */}
						{zakEnabled && (
							<div className="flex items-start space-x-3 ml-8">
								<Checkbox
									id="zak-auto-mark-paid"
									checked={zakAutoMarkPaid == true}
									onCheckedChange={
										handleZakAutoMarkPaidChange
									}
									className="mt-1"
								/>
								<div className="space-y-1">
									<Label
										htmlFor="zak-auto-mark-paid"
										className="text-sm font-medium cursor-pointer"
									>
										ZAK Rechnungen automatisch als bezahlt
										markieren
									</Label>
									<p className="text-xs text-gray-600">
										Wenn aktiviert, werden Rechnungen
										automatisch als bezahlt markiert, wenn
										sie erfolgreich an die ZAK gesendet
										wurden.
									</p>
								</div>
							</div>
						)}

						{/* ZAK Download Archive Setting */}
						{zakEnabled && (
							<div className="flex items-start space-x-3 ml-8">
								<Checkbox
									id="zak-download-archive"
									checked={zakDownloadArchive == true}
									onCheckedChange={
										handleZakDownloadArchiveChange
									}
									className="mt-1"
								/>
								<div className="space-y-1">
									<Label
										htmlFor="zak-download-archive"
										className="text-sm font-medium cursor-pointer"
									>
										ZAK Archiv automatisch herunterladen
									</Label>
									<p className="text-xs text-gray-600">
										Wenn aktiviert, wird das erstellte
										ZIP-Archiv automatisch heruntergeladen,
										nachdem die Rechnungen erfolgreich an
										die ZAK gesendet wurden.
									</p>
								</div>
							</div>
						)}

						{/* Catalog Only Mode Setting */}
						<div className="flex items-start space-x-3">
							<Checkbox
								id="catalog-only-mode"
								checked={catalogOnlyMode == true}
								onCheckedChange={handleCatalogOnlyModeChange}
								className="mt-1"
								data-testid="config-catalog-only-mode"
							/>
							<div className="space-y-1">
								<Label
									htmlFor="catalog-only-mode"
									className="text-sm font-medium cursor-pointer"
								>
									Nur Leistungskatalog verwenden
								</Label>
								<p className="text-xs text-gray-600">
									Wenn aktiviert, werden in der
									Auftragsansicht nur Positionen aus dem
									Leistungskatalog angeboten. Die einzelnen
									Tarif-, Artikel- und Vorlagen-Buttons werden
									ausgeblendet. Das gilt nur wenn ein
									Leistungskatalog angelegt wurde.
								</p>
							</div>
						</div>

						{/* Technician Assignment Scope Setting */}
						<div className="flex items-start space-x-3">
							<Checkbox
								id="technician-applies-to-articles"
								checked={technicianAppliesToArticles == true}
								onCheckedChange={
									handleTechnicianAppliesToArticlesChange
								}
								className="mt-1"
								data-testid="config-technician-applies-to-articles"
							/>
							<div className="space-y-1">
								<Label
									htmlFor="technician-applies-to-articles"
									className="text-sm font-medium cursor-pointer"
								>
									Techniker auch bei Artikelpositionen
									übernehmen
								</Label>
								<p className="text-xs text-gray-600">
									Wenn aktiviert, wird der gewählte
									Standard-Techniker auch auf
									Artikelpositionen übernommen. Andernfalls
									gilt er nur für Tarifpositionen.
								</p>
							</div>
						</div>

						{/* Patient Divergence Warning Setting */}
						<div className="flex items-start space-x-3">
							<Checkbox
								id="patient-divergence-enabled"
								checked={patientDivergenceEnabled == true}
								onCheckedChange={
									handlePatientDivergenceEnabledChange
								}
								className="mt-1"
								data-testid="config-patient-divergence-enabled"
							/>
							<div className="space-y-1">
								<Label
									htmlFor="patient-divergence-enabled"
									className="text-sm font-medium cursor-pointer"
								>
									Warnung bei möglichem Patientenwechsel
									aktivieren
								</Label>
								<p className="text-xs text-gray-600">
									Zeigt einen Hinweis an, wenn beim Bearbeiten
									eines Patienten Vorname, Nachname, Code oder
									Geburtsdatum stark vom ursprünglichen Wert
									abweichen — als Schutz davor, versehentlich
									die Daten eines anderen Patienten zu
									überschreiben.
								</p>
							</div>
						</div>

						{patientDivergenceEnabled && (
							<div className="flex items-start space-x-3 ml-8">
								<div className="space-y-1 w-full">
									<Label
										htmlFor="patient-divergence-threshold"
										className="text-sm font-medium"
									>
										Erlaubte Menge an Änderungen
									</Label>
									<Input
										id="patient-divergence-threshold"
										type="number"
										min={1}
										value={thresholdInput}
										onChange={(e) =>
											setThresholdInput(e.target.value)
										}
										onBlur={
											handlePatientDivergenceThresholdCommit
										}
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												(
													e.target as HTMLInputElement
												).blur();
											}
										}}
										className="max-w-[120px]"
										data-testid="config-patient-divergence-threshold"
									/>
									<p className="text-xs text-gray-600">
										Anzahl der Eingaben in Patientenfeldern,
										die ohne Warnung erlaubt sind. Höhere
										Werte = unempfindlicher. Standard: 2.
									</p>
								</div>
							</div>
						)}

						{patientDivergenceEnabled && (
							<div className="flex items-start space-x-3 ml-8">
								<Checkbox
									id="patient-divergence-ignore-code"
									checked={
										patientDivergenceIgnoreCode == true
									}
									onCheckedChange={
										handlePatientDivergenceIgnoreCodeChange
									}
									className="mt-1"
									data-testid="config-patient-divergence-ignore-code"
								/>
								<div className="space-y-1">
									<Label
										htmlFor="patient-divergence-ignore-code"
										className="text-sm font-medium cursor-pointer"
									>
										Code-Feld bei Warnung ignorieren
									</Label>
									<p className="text-xs text-gray-600">
										Da der automatisch generierte Code
										häufig komplett überschrieben wird, kann
										er von der Warnung ausgeschlossen
										werden.
									</p>
								</div>
							</div>
						)}

						{/* Background Double-Click Fullscreen Setting */}
						<div className="flex items-start space-x-3">
							<Checkbox
								id="background-double-click-fullscreen"
								checked={
									backgroundDoubleClickFullscreenEnabled ==
									true
								}
								onCheckedChange={
									handleBackgroundDoubleClickFullscreenChange
								}
								className="mt-1"
								data-testid="config-background-double-click-fullscreen"
							/>
							<div className="space-y-1">
								<Label
									htmlFor="background-double-click-fullscreen"
									className="text-sm font-medium cursor-pointer"
								>
									Doppelklick auf Hintergrund öffnet Vollbild
								</Label>
								<p className="text-xs text-gray-600">
									In der Auftragsansicht öffnet ein
									Doppelklick auf den freien Bereich eines
									Panels den Vollbildmodus. Wenn deaktiviert,
									ist der Vollbildmodus nur über den
									Vollbild-Button erreichbar.
								</p>
							</div>
						</div>

						{/* Untouched Jobs Strict Mode Setting */}
						<div className="flex items-start space-x-3">
							<Checkbox
								id="untouched-jobs-require-no-documents"
								checked={
									untouchedJobsRequireNoDocuments == true
								}
								onCheckedChange={
									handleUntouchedJobsRequireNoDocumentsChange
								}
								className="mt-1"
								data-testid="config-untouched-jobs-require-no-documents"
							/>
							<div className="space-y-1">
								<Label
									htmlFor="untouched-jobs-require-no-documents"
									className="text-sm font-medium cursor-pointer"
								>
									Unbearbeitete Aufträge: nur ohne Dokumente
								</Label>
								<p className="text-xs text-gray-600">
									Strenger Modus für die Kategorie
									"Unbearbeitete Aufträge" auf dem Dashboard.
									Wenn aktiviert, gilt ein Auftrag nur dann
									als unbearbeitet, wenn er noch keinerlei
									Dokumente besitzt. Standardmässig genügt es,
									wenn Kostenvoranschlag, Lieferschein und
									Gutschrift keine Positionen enthalten.
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
